import { selectBonuses } from "@/entities/bonus"
import { selectTradingWallet, selectTradingWalletLoading } from "@/entities/trading"
import { useAppSelector } from "@/shared/lib/redux"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { Table } from "@/shared/ui/table"
import { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"
import { DebitButton } from "../debit-button"
import { selectCommunityInfo } from "@/entities/community"
import formatMediumFont from "@/shared/lib/format-medium-font"
import { toLocaleString } from "@/shared/lib/to-locale-string"

export const WalletTable = () => {
    const wallet = useAppSelector(selectTradingWallet)
    const { type } = useAppSelector(selectCommunityInfo)
    const bonuses = useAppSelector(selectBonuses)
    const parsedWallet = useMemo(() => {
        if (type === 'foundation') {
            return Object.entries(wallet)
        }
        return Object
            .entries(wallet)
            .filter(([bonusId]) => !bonuses.find(bonus => bonus.id === bonusId && bonus.symbol.toLocaleLowerCase() === 'bon'))
    }, [wallet, bonuses, type])
    const isLoading = useAppSelector(selectTradingWalletLoading)
    const balance = useMemo(() => parseFloat(Object.values(wallet).reduce((acc, curr) => acc += curr.amount, 0).toFixed(2)),[wallet])

    const walletColumns: ColumnDef<typeof parsedWallet[0], any>[] = [
        {
            accessorFn: ([bonusId]) => (
                <div className="text-white font-semibold text-[15px]">
                    {bonuses.find(bonus => bonus.id === bonusId)?.name}
                </div>
            ),
            id: 'name',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-start text-nowrap">Название</div>,
        },
        {
            accessorFn: ([bonusId, wallet]) => (
                <div className="flex justify-end">
                    <div className="flex items-center gap-[6px]">
                        <div className="text-white font-semibold text-[15px]">
                            {toLocaleString(wallet.amount)}
                        </div>
                        <div className="w-9 h-9 rounded-full overflow-hidden">
                            <img src={bonuses.find(bonus => bonus.id === bonusId)?.image} className="w-full h-full object-contain" alt="" />
                        </div>
                    </div>
                </div>
            ),
            id: 'amount',
            enableSorting: false,
            cell: info => info.getValue(),
            header: () => <div className="!text-end text-nowrap">Сумма бонусов</div>,
        },
    ]

    return (
        <>
            {Boolean(parsedWallet.length) &&
                <div className="p-5 flex-1 !bg-color-dark rounded-2xl screen:rounded-3xl">
                    <div className="pt-2 flex flex-col">
                        <div className="font-mediumttf text-white text-[30px]">
                            Кошелек
                        </div>
                        <div className="flex flex-col gap-2 screen:flex-row screen:justify-between screen:items-center">
                            <div className="text-white font-mediumttf">
                                <span className="text-white/70">Баланс</span>
                                &nbsp;
                                {formatMediumFont(toLocaleString(balance), 'text-2xl')}
                            </div>
                            <div>
                                {type === 'foundation' &&
                                    <DebitButton />
                                }
                            </div>
                        </div>
                    </div>
                    <div className="h-full no-scrollbar">
                        <Table
                            columns={walletColumns}
                            data={parsedWallet}
                            classNames={{
                                table: '[&_th]:!border-[#E7EAEE88] [&_td]:!border-[#E7EAEE44]',
                                thead: 'table table-fixed w-full',
                                tbody: 'max-h-[400px] overflow-y-scroll block [&_tr]:table [&_tr]:table-fixed [&_tr]:w-full !w-full'
                            }}
                        />
                    </div>
                </div>
            }
            {(!Boolean(parsedWallet.length) && isLoading) &&
                <div className="info_panel !bg-color-dark">
                    <Loader/>
                </div>
            }
            {(!Boolean(parsedWallet.length) && !isLoading) &&
                <NoTableData className="!bg-color-dark text-white" label="Нет данных о балансе" />
            }
        </>
    )
}